import * as React from 'react';

import { enumRouteStatus } from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Button, Col, Menu, MenuItem, Row, TextField } from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { MoreHorizontal } from 'lucide-react';

import { useRouteTransitionMutation } from '../../hooks/mutations/useRouteTransitionMutation';

import { RouteFormState } from '~/modules/transportation/types';

type RouteStatusActionsProps = {
  route: RouteFormState & { id: string };
  onSuccess: () => void;
  onClose: () => void;
};

const TERMINAL_STATUSES = [
  enumRouteStatus.Voided,
  enumRouteStatus.Completed,
  enumRouteStatus.Failed,
] as string[];

export const RouteStatusActions = ({
  route,
  onSuccess,
  onClose,
}: RouteStatusActionsProps) => {
  const t = useTranslations();
  const toast = useToast();

  const { mutateAsync: transitionRoute, isPending: isTransitioning } =
    useRouteTransitionMutation();

  const anyPending = isTransitioning;

  const handleAction = async (
    action: () => Promise<unknown>,
    successMessage: string,
  ) => {
    try {
      await action();
      toast.success(successMessage);
      onSuccess();
      onClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.routesView.form.messages.error,
      );
    }
  };

  const handleActivate = () =>
    handleAction(
      () =>
        transitionRoute({
          id: route.id,
          status: enumRouteStatus.Active,
        }),
      t.routesView.form.messages.activateSuccess,
    );

  const handleComplete = () =>
    handleAction(
      () =>
        transitionRoute({
          id: route.id,
          status: enumRouteStatus.Completed,
        }),
      t.routesView.form.messages.completeSuccess,
    );

  const handleVoid = (reason: string) =>
    handleAction(
      () =>
        transitionRoute({
          id: route.id,
          status: enumRouteStatus.Voided,
          notes: reason,
        }),
      t.routesView.form.messages.voidSuccess,
    );

  const handleFail = (reason: string) =>
    handleAction(
      () =>
        transitionRoute({
          id: route.id,
          status: enumRouteStatus.Failed,
          notes: reason,
        }),
      t.routesView.form.messages.failSuccess,
    );

  const getPrimaryAction = () => {
    switch (route.status) {
      case enumRouteStatus.Planned:
        return {
          label: t.routesView.form.buttons.activate,
          color: 'primary' as const,
          variant: 'solid' as const,
          onPress: handleActivate,
        };
      case enumRouteStatus.Active:
        return {
          label: t.routesView.form.buttons.complete,
          color: 'success' as const,
          variant: 'solid' as const,
          onPress: handleComplete,
        };
      default:
        return null;
    }
  };

  const getMenuItems = (): MenuItem[] => {
    const items: MenuItem[] = [];
    const status = route.status as string;

    const canVoid = !TERMINAL_STATUSES.includes(status);
    if (canVoid) {
      items.push({
        label: t.routesView.form.buttons.void,
        className: 'text-failure',
        secondStep: ({ close }) => (
          <ReasonInputStep
            title={t.servicesView.form.reasonPrompts.voidReason}
            placeholder={t.servicesView.form.reasonPrompts.reasonPlaceholder}
            required
            onConfirm={(reason) => {
              close();
              handleVoid(reason);
            }}
            onCancel={close}
          />
        ),
      });
    }

    const canFail = status === enumRouteStatus.Active;
    if (canFail) {
      items.push({
        label: t.routesView.form.buttons.fail,
        className: 'text-failure',
        secondStep: ({ close }) => (
          <ReasonInputStep
            title={t.servicesView.form.reasonPrompts.failReason}
            placeholder={t.servicesView.form.reasonPrompts.reasonPlaceholder}
            required
            onConfirm={(reason) => {
              close();
              handleFail(reason);
            }}
            onCancel={close}
          />
        ),
      });
    }

    return items;
  };

  const primaryAction = getPrimaryAction();
  const menuItems = getMenuItems();

  return (
    <>
      {menuItems.length > 0 && (
        <Menu
          trigger={
            <Button
              icon={<MoreHorizontal />}
              variant="outline"
              isDisabled={anyPending}
            />
          }
          items={menuItems}
        />
      )}
      {primaryAction && (
        <Button
          color={primaryAction.color}
          variant={primaryAction.variant}
          onPress={primaryAction.onPress}
          isPending={anyPending}
          isDisabled={anyPending}
        >
          {primaryAction.label}
        </Button>
      )}
    </>
  );
};

type ReasonInputStepProps = {
  title: string;
  placeholder: string;
  required: boolean;
  onConfirm: (reason: string) => void;
  onCancel: () => void;
};

const ReasonInputStep = ({
  title,
  placeholder,
  required,
  onConfirm,
  onCancel,
}: ReasonInputStepProps) => {
  const t = useTranslations();
  const [reason, setReason] = React.useState('');

  return (
    <Col gap3 className="@ReasonInputStep">
      <div className="text-sm font-medium">{title}</div>
      <TextField
        value={reason}
        onChange={setReason}
        placeholder={placeholder}
        aria-label={title}
      />
      <Row gap2>
        <Button variant="outline" color="neutral" onPress={onCancel}>
          {t.routesView.form.buttons.cancel}
        </Button>
        <Button
          variant="solid"
          color="primary"
          isDisabled={required && !reason.trim()}
          onPress={() => onConfirm(reason.trim())}
        >
          {t.servicesView.form.reasonPrompts.confirm}
        </Button>
      </Row>
    </Col>
  );
};
